/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useCallback } from "react";
import useSelect from "~components/Form/hooks/useSelect";
import prepareForOptions from "~services/utils/prepareForOptions";

const usePesquisaVigente = (formikRef, valores = [], config = {}, onFilterChange) => {
  const { salvarLocalStorage = false, chave } = config;
  const pesquisaSelectVigente = useSelect("pesquisa");

  const [pesquisa, setPesquisa] = useState(0);
  const [fila, setFila] = useState(null);
  const [dados, setDados] = useState(valores || []);
  const [initialized, setInitialized] = useState(false);

  // Evita atualizações desnecessárias dos dados
  useEffect(() => {
    if (JSON.stringify(valores) !== JSON.stringify(dados)) {
      setDados(valores || []);
    }
  }, [valores]);

  const optionsPesquisaVigente = useMemo(
    () => prepareForOptions(dados, ["label"], "value"),
    [dados]
  );

  const pesquisasObjId = useMemo(
    () => createObj({ key: "id", data: dados, prefix: "key-" }),
    [dados]
  );

  const pesquisasObjCodigo = useMemo(
    () => createObj({ key: "codigo", data: dados }),
    [dados]
  );

  const pesquisasObjNome = useMemo(
    () => createObj({ key: "nome", data: dados }),
    [dados]
  );

  function createObj({ key = "codigo", data = [], prefix = "" }) {
    if (Array.isArray(data)) {
      return data.reduce((acc, cur) => {
        acc[`${prefix}${cur[key]}`] = cur;
        return acc;
      }, {});
    }
    return {};
  }

  // useCallback para evitar recriação da função
  const onChangePesquisaVigente = useCallback((e) => {
    pesquisaSelectVigente.upDateValue(e.value);
    pesquisaSelectVigente.upDateDefaultValue(e || "");
    formikRef?.current?.setFieldValue("validityPeriod", e.value);

    if (salvarLocalStorage) {
      localStorage.setItem(chave, JSON.stringify(e));
    }

    onFilterChange((prev) => ({
      ...prev,
      validityPeriod: e.value,
    }));

    setTimeout(() => formikRef?.current?.setFieldTouched("validityPeriod", true));
    setPesquisa(e.value);
  }, [salvarLocalStorage, chave, onFilterChange, formikRef]);

  // Efeito para inicialização do localStorage - executa apenas uma vez
  useEffect(() => {
    if (salvarLocalStorage && !initialized) {
      const pesquisaSalva = localStorage.getItem(chave);
      if (pesquisaSalva) {
        const pesquisaObj = JSON.parse(pesquisaSalva);
        setPesquisa(pesquisaObj.value);
        setFila(pesquisaObj.value);
        // Não chama onChangePesquisaVigente aqui diretamente
      }
      setInitialized(true);
    }
  }, [salvarLocalStorage, chave, initialized]);

  // Efeito separado para processar a fila quando options estiverem prontos
  useEffect(() => {
    if (fila && optionsPesquisaVigente?.length > 0 && initialized) {
      const pesquisa = optionsPesquisaVigente.find((p) => p.value === fila);
      if (pesquisa) {
        // Usa setTimeout para evitar atualização síncrona durante render
        setTimeout(() => {
          onChangePesquisaVigente(pesquisa);
        });
      }
      setFila(null);
    }
  }, [fila, optionsPesquisaVigente, initialized, onChangePesquisaVigente]);

  function setSelectPesquisa(id) {
    if (optionsPesquisaVigente?.length > 0) {
      const pesquisa = optionsPesquisaVigente.find((p) => p.value === id);
      if (pesquisa) onChangePesquisaVigente(pesquisa);
      setFila(null);
    } else {
      setFila(id);
    }
  }

  return {
    optionsPesquisaVigente,
    dados,
    fila,
    onChangePesquisaVigente,
    setSelectPesquisa,
    pesquisa,
    pesquisaSelectVigente,
    pesquisasObjId,
    pesquisasObjCodigo,
    pesquisasObjNome,
  };
};

export default usePesquisaVigente;